import {useEffect,useMemo,useState} from 'react'
import {useSelector} from 'react-redux'

import {
	usePayments,
	useSessions,
	usePayoutSummary,
	useActiveSubscriptions,
	useFinancialsDashboardTabs
} from './hooks'

import {envAwarePost} from '../../util/util'
import {useDefaultCurrency} from '../settings/hooks'
import UserThumbnail from '../UserThumbnail/UserThumbnail'
import LocaleCurrency from '../LocaleCurrency/LocaleCurrency'
import LocaleDate from '../LocaleDate/LocaleDate'
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'

import styles from './FinancialsDashboard.module.css'

import useCurrentUser from '../../hooks/useCurrentUser'

const RecentPayments = ()=>{
	const {status,payments} = usePayments()
	const {setActiveTab} = useFinancialsDashboardTabs()
	const PaymentSummary = ({payment})=>(
		<div>
			<UserThumbnail src={payment.client_profile_pic}/>
			<small>
				<strong>{payment.client_user_name}</strong> 
				<span className="mx-1">paid</span>
				<strong>
					<LocaleCurrency ISO={payment.currency_ISO} amount={payment.amount}/>
				</strong>
				<span className="mx-1">on</span>
				<strong>
					<LocaleDate date={payment.date}/>
				</strong>
			</small>
		</div>
	)

	return (
		<div className="rounded border p-2">
			<div className="d-flex align-items-center justify-content-between">
				<strong>Recent Payments</strong>
				<Button onClick={()=>{setActiveTab('payments')}} size="sm" variant="link">
					View All Payments
				</Button>
			</div>
			<hr className="my-1"/>
			{ status === 'pending' ?
				<div>
					Loading payments <Spinner animation="border" size="sm"/>
				</div>
				:
				payments.slice(0,5).map(p=>(
					<PaymentSummary payment={p}/>
				))
			}
		</div>
	)
}

const RecentSessionPurchases = ()=>{
	const {status,sessions,dateAggregates} = useSessions()
	const {setActiveTab} = useFinancialsDashboardTabs()
	const sessionsOwed = useMemo(()=>sessions.reduce(
		(carry,session) => carry + (session.is_completed?0:1),
		0
	),[sessions])

	const SessionAggregate = ({agg})=>(
		<div>
			<UserThumbnail src={agg.client_profile_pic}/>
			<small className="d-inline-flex align-items-center">
				<strong>{agg.client_user_name}</strong> 
				<span className="mx-1">purchased <strong><span className="text-success">{agg.num_sessions}</span></strong> on </span>
				<strong>
					<LocaleDate date={new Date(`${agg.date}T00:00:00`)} includeTime={false}/>
				</strong>
			</small>
		</div>
	)

	return (
		<div className="rounded border p-2">
			<div className="d-flex align-items-center justify-content-between">
				<strong>Recent Session Purchases</strong>
				<Button onClick={()=>{setActiveTab('sessions')}} size="sm" variant="link">
					Manage Sessions
				</Button>
			</div>
			<hr className="my-1"/>
			{ status !== 'pending' &&
				<>
					<small className="d-block text-center">
						You have <span className="text-info">{sessionsOwed}</span> training sessions to fulfill
					</small>
					<hr className="my-1"/>
				</>
			}
			{ status === 'pending' ?
				<div>
					Loading sessions <Spinner animation="border" size="sm"/>
				</div>
				:
				<div>
					{dateAggregates.slice(0,5).map(agg=>(
						<SessionAggregate agg={agg}/>
					))}
				</div>
			}
		</div>
	)
}

const ActiveSubscriptionsOverview = ()=>{
	const {status,subscriptions,monthlyEstimate} = useActiveSubscriptions()
	const {setActiveTab} = useFinancialsDashboardTabs()
	const defaultCurrency = useDefaultCurrency()
	const SubscriptionSummary = ({subscription})=>(
		<div>
			<UserThumbnail src={subscription.client_profile_pic}/>
			<small className="d-inline-flex align-items-center">
				<strong>{subscription.client_user_name}</strong> 
				<span className="mx-1">subscribed to</span>
				<strong>
					{subscription.product_name} @ <LocaleCurrency amount={(subscription.payment_amount/100).toFixed()} ISO={defaultCurrency.ISO}/> {subscription.payment_interval}
				</strong>
			</small>
		</div>
	)
	return (
		<div className="rounded border p-2">
			<div className="d-flex align-items-center justify-content-between">
				<strong>Subscriptions Summary</strong>
				<Button onClick={()=>{setActiveTab('subscriptions')}} size="sm" variant="link">
					Manage Subscriptions
				</Button>
			</div>
			<hr className="my-1"/>
			{status!=='pending'&&
				<>
					<small className="d-block text-center">
						Estimated <span className="text-success"><LocaleCurrency amount={monthlyEstimate} ISO={defaultCurrency.ISO}/></span> income over the next 30 days
					</small>
					<hr className="my-1"/>
				</>
			}
			{status==='pending'?
				<div>
					Loading subscriptions <Spinner animation="border" size="sm"/>
				</div>
				:
				<>
					{subscriptions.slice(0,5).map(s=>
						<SubscriptionSummary subscription={s}/>
					)}
					{((subscriptions.length - 5) > 0)&&
						<div className="text-center text-muted">
							<small>{subscriptions.length - 5} active subscriptions not shown</small>
						</div>
					}
				</>
			}
		</div>
	)
}

const PayoutSummary = ()=>{
	const {
		status,
		availableBalance,
		pendingBalance,
		past90Total
	} = usePayoutSummary()
	const stripePayoutUrl = useSelector(state=>state.users.current_user.stripe_account.payout_dashboard_url)

	const {ISO} = useDefaultCurrency()

	return (
		<div className="rounded border p-2">
			<div className="d-flex align-items-center justify-content-between mb-2">
				<strong>Payouts Summary</strong>
				<small><a href={stripePayoutUrl} target="_blank" rel="noopener">View Payout Dashboard</a></small>
			</div>
			{status==='pending'?
				<div>
					<hr className="my-1"/>
					Loading payout summary <Spinner animation="border" size="sm"/>
				</div>
				:
				<>
					<Table className="mb-0" striped>
						<tbody>
							<tr>
								<td>
									Previous 90 day total
								</td>
								<td>
									<span className="text-info"><LocaleCurrency amount={past90Total} ISO={ISO}/></span>
								</td>
							</tr>
							<tr>
								<td>
									Available balance
								</td>
								<td>
									<span className="text-success"><LocaleCurrency amount={availableBalance} ISO={ISO}/></span>
								</td>
							</tr>
							<tr>
								<td>
									Pending balance
								</td>
								<td>
									<span className="text-warning"><LocaleCurrency amount={pendingBalance} ISO={ISO}/></span>
								</td>
							</tr>
						</tbody>
					</Table>
				</>
			}
		</div>
	)
}

const PaymentTips = ()=>{

    const pageAddress = useSelector(state => state.users.current_user.bio_data.pageAddress);

	const user = useCurrentUser()
	const pageTypeText = (
		user.account_type === 'gym'
		? 'Gym'
		: 'Trainer'
	)   

	return (
		<div id={styles.PaymentTips} className="rounded border p-2">
			<strong> Your clients can make a payment one of three ways</strong>
			<hr className="my-1"/>
			<ul>
				<li className="my-1"> Going to your public {pageTypeText} Page (<a target="_blank" href={`https://www.fitsw.com/${pageAddress}`}> {`https://www.fitsw.com/${pageAddress}`} </a>), there is a payment area at the bottom of the page.</li>
				<li className="my-1"> Logging into FitSW.com with their account and going to their Payments tab.</li>
				<li className="my-1"> Logging into the FitSW mobile app and going to their Financials menu item.</li>
			</ul>
		</div>
	)
}

const Overview = ()=>{
	
	return (
		<div style={{height:'100%',overflow:'auto'}}> 
			<div id={styles.Overview}>
				<PayoutSummary/>
				<RecentPayments/>
				<RecentSessionPurchases/>
				<ActiveSubscriptionsOverview/>
				<PaymentTips/>
			</div>
		</div>
	)
}

export default Overview